import { FunctionComponent } from 'react'
import { Box, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { IGhgTable, IScope3CategoryCompleteness } from '../../../../shared/services/generated-report/generated-report.model';
import { ReportingGhgTableSegment } from './reporting-ghg-table-segment';
import { ReportingDataQualityMix } from '../reporting-data-quality-mix';
import { ReportingReductionDisclosures } from '../reporting-reduction-disclosures';
import { describeWttExclusion } from '../../../../shared/utils/wtt-exclusion-utils';

const categoryFigure = (category?: IScope3CategoryCompleteness) =>
  category && category.status === 'HAS_DATA' ? category.tco2e.toFixed(4) : '—'

// A comparative year with no category figures (Scope 3 entered as a single total) reads n/a.
const comparativeFigure = (categories: IScope3CategoryCompleteness[] | null | undefined, number: number) =>
  Array.isArray(categories) ? categoryFigure(categories.find((c) => c.number === number)) : 'n/a'

export const ReportingGhgTable: FunctionComponent<{ tableData: IGhgTable }> = ({ tableData }) => {
  const comparatives = tableData?.scope3CompletenessComparatives
  const singleTotalYear = !!tableData && (
    !Array.isArray(comparatives?.base) || (!!tableData.reportPeriods.previous && !Array.isArray(comparatives?.previous))
  )

  return (
    <Box sx={{
      mb: 2, overflowX: 'auto',
      maxWidth: '100%'
    }}>
      {tableData &&
        <TableContainer>
          <Table size="small" aria-label="a dense table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '1rem', color: 'primary.dark', borderBottomColor: 'primary.dark', borderBottomWidth: '3px', borderBottomStyle: 'solid', minWidth: '250px' }}></TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '1rem', color: 'primary.dark', borderBottomColor: 'primary.dark', borderBottomWidth: '3px', borderBottomStyle: 'solid' }}>
                  <Chip label={`Base Year ${`(${tableData.reportPeriods.base})`}`} sx={{ fontWeight: 600, fontSize: '1.15rem', color: 'primary.dark', backgroundColor: 'white', borderColor: 'primary.dark', borderStyle: 'solid', borderWidth: '1px' }} /></TableCell>
                {tableData.reportPeriods.previous && <TableCell align="center" sx={{ fontWeight: 600, fontSize: '1rem', color: 'primary.dark', borderBottomColor: 'primary.dark', borderBottomWidth: '3px', borderBottomStyle: 'solid' }}>
                  <Chip label={`Previous Year ${`(${tableData.reportPeriods.previous})`}`} sx={{ fontWeight: 600, fontSize: '1.15rem', color: 'primary.dark', backgroundColor: 'lightGrey.main' }} /></TableCell>}
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '1rem', color: 'primary.dark', borderBottomColor: 'primary.dark', borderBottomWidth: '3px', borderBottomStyle: 'solid' }}>
                  <Chip label={`Current Year ${`(${tableData.reportPeriods.current})`}`} sx={{ fontWeight: 600, fontSize: '1.15rem', color: 'primary.dark', backgroundColor: 'secondary.light' }} /></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell scope='row' sx={{ fontWeight: 600, borderBottomColor: 'primary.dark', borderBottomWidth: '2px', borderBottomStyle: 'solid', borderTopColor: 'primary.dark', borderTopWidth: '2px', borderTopStyle: 'solid' }} >
                  Total
                </TableCell>
                <TableCell colSpan={8} sx={{ borderBottomColor: 'primary.dark', borderBottomWidth: '2px', borderBottomStyle: 'solid', borderTopColor: 'primary.dark', borderTopWidth: '2px', borderTopStyle: 'solid' }}></TableCell>
              </TableRow>
              <ReportingGhgTableSegment title={<>
                Scope 1 GHG emissions (tCO<sub>2</sub>e)
              </>} data={tableData.scopeOne}
                showPrevious={!!tableData.reportPeriods.previous}
              />
              <ReportingGhgTableSegment title={<>
                Scope 2 Market-based GHG emissions (tCO<sub>2</sub>e)
              </>} data={tableData.scopeTwoMarket}
                showPrevious={!!tableData.reportPeriods.previous} />
              <ReportingGhgTableSegment title={<>
                Scope 2 Location-based GHG emissions (tCO<sub>2</sub>e)
              </>} data={tableData.scopeTwoLocation}
                showPrevious={!!tableData.reportPeriods.previous} />
              <ReportingGhgTableSegment title={<>
                Scope 3 GHG emissions (tCO<sub>2</sub>e)
              </>} data={tableData.scopeThree}
                showPrevious={!!tableData.reportPeriods.previous} />
              <ReportingGhgTableSegment title={<>
                Total Market-based GHG emissions (tCO<sub>2</sub>e)
              </>} data={tableData.totalMarket}
                showPrevious={!!tableData.reportPeriods.previous} />
              <ReportingGhgTableSegment title={<>
                Total Location-based GHG emissions (tCO<sub>2</sub>e)
              </>} data={tableData.totalLocation}
                showPrevious={!!tableData.reportPeriods.previous} />
              <TableRow>
                <TableCell scope='row' sx={{ fontWeight: 600, borderBottomColor: 'primary.dark', borderBottomWidth: '2px', borderBottomStyle: 'solid', borderTopColor: 'primary.dark', borderTopWidth: '2px', borderTopStyle: 'solid' }} >
                  Group Breakdown
                </TableCell>
                <TableCell colSpan={8} sx={{ borderBottomColor: 'primary.dark', borderBottomWidth: '2px', borderBottomStyle: 'solid', borderTopColor: 'primary.dark', borderTopWidth: '2px', borderTopStyle: 'solid' }}></TableCell>
              </TableRow>
              {/* Scope 2's group is location-based, like the headline totals. An archived year shows the
                  groups its breakdown covers and a dash for the rest. */}
              {tableData.breakdownPerGroup.map(data => {
                return <ReportingGhgTableSegment title={<>{`${data.groupName} `}
                  {data.scope === '2' && '(location-based) '}GHG emissions (tCO<sub>2</sub>e)
                </>} data={data}
                  showPrevious={!!tableData.reportPeriods.previous} />
              })}
            </TableBody>
          </Table>
        </TableContainer>}
      {/* Scope 3 by GHG Protocol category: base, previous and current, with the current year's
          completeness status. A year whose Scope 3 was entered as a single total (an archived year
          with no breakdown) has no category figures and shows n/a. */}
      {tableData && (tableData.scope3Completeness?.length ?? 0) > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: 'common.black', fontSize: '1.125rem' }}>
            Scope 3 by category
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Base Year ({tableData.reportPeriods.base}) tCO<sub>2</sub>e</TableCell>
                  {tableData.reportPeriods.previous && (
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Previous Year ({tableData.reportPeriods.previous}) tCO<sub>2</sub>e</TableCell>
                  )}
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Current Year ({tableData.reportPeriods.current}) tCO<sub>2</sub>e</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(tableData.scope3Completeness ?? []).map((c) => (
                  <TableRow key={c.number}>
                    <TableCell>{c.number}. {c.label}</TableCell>
                    <TableCell align="right">{comparativeFigure(comparatives?.base, c.number)}</TableCell>
                    {tableData.reportPeriods.previous && (
                      <TableCell align="right">{comparativeFigure(comparatives?.previous, c.number)}</TableCell>
                    )}
                    <TableCell align="right">{categoryFigure(c)}</TableCell>
                    <TableCell align="right">
                      <Chip
                        size="small"
                        label={c.status === 'HAS_DATA' ? 'Reported' : 'Not reported'}
                        color={c.status === 'HAS_DATA' ? 'success' : 'default'}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {singleTotalYear && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
              n/a: that year&apos;s Scope 3 was recorded as a single total, without a breakdown by category.
            </Typography>
          )}
        </Box>
      )}
      {/* Data-quality mix for the requested period (tier share of each reported total). */}
      {tableData && tableData.dataQualityMix && (tableData.dataQualityMix.totalLocation?.totalTco2e > 0 || tableData.dataQualityMix.totalMarket?.totalTco2e > 0) && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: 'common.black', fontSize: '1.125rem' }}>
            Data quality
          </Typography>
          <ReportingDataQualityMix mix={tableData.dataQualityMix} />
        </Box>
      )}
      {/* TICKET-D-a / E: GHG Protocol disclosures: GWP basis, biogenic CO2 reported outside of scopes,
          and well-to-tank emissions the period holds but does not count. */}
      {tableData && ((tableData.gwpVersions?.length ?? 0) > 0 ||
        (tableData.biogenicCo2?.locationBased ?? 0) > 0 ||
        (tableData.biogenicCo2?.marketBased ?? 0) > 0 ||
        !!describeWttExclusion(tableData.wttExclusion)) && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 600, color: 'common.black', fontSize: '1.125rem' }}>
            Greenhouse gas disclosures
          </Typography>
          {(tableData.gwpVersions?.length ?? 0) > 0 && (
            <Typography variant="body2" sx={{ mb: 1, color: 'common.black' }}>
              GWP basis: {(tableData.gwpVersions ?? []).join(', ')}
            </Typography>
          )}
          {((tableData.biogenicCo2?.locationBased ?? 0) > 0 || (tableData.biogenicCo2?.marketBased ?? 0) > 0) && (
            <Typography variant="body2" sx={{ color: 'common.black' }}>
              Biogenic CO<sub>2</sub> (reported outside of scopes):{' '}
              {(tableData.biogenicCo2?.locationBased ?? 0).toFixed(2)} tCO<sub>2</sub>e (location-based) /{' '}
              {(tableData.biogenicCo2?.marketBased ?? 0).toFixed(2)} tCO<sub>2</sub>e (market-based)
            </Typography>
          )}
          {describeWttExclusion(tableData.wttExclusion) && (
            <Typography variant="body2" sx={{ mt: 1, color: 'common.black' }}>
              {/* Same words as the PDF and audit pack, with the 2 set as a subscript like the lines above. */}
              {(describeWttExclusion(tableData.wttExclusion) ?? '').split('tCO2e').map((part, index) => (
                <span key={index}>
                  {index > 0 && <>tCO<sub>2</sub>e</>}
                  {part}
                </span>
              ))}
            </Typography>
          )}
        </Box>
      )}
      {/* Supplementary reduction disclosures: targets/plan coverage + the offsets register.
          Scope totals above stay gross; the net figure is an informational memo only. */}
      {tableData && tableData.reductionDisclosures && (
        <ReportingReductionDisclosures disclosures={tableData.reductionDisclosures} />
      )}
    </Box >
  )
}